import { removeUnallowedFields } from '@ragu-components/ord-components';
import { CalendarEvents } from '@ragu-components/ord-resources';
import { PersonalCalendarEventUpdateFields } from '@ragu-components/ord-types';

import { dateConverterFromInputToServer, dateConverterFromServerToInput } from 'src/views/Resources/personal';

import type { CalendarEvent, PersonalCalendarEvent } from '@ragu-components/ord-types';
import type { ResourceCallbacks } from 'react-admin';

const processBeforeSave = async (record: PersonalCalendarEvent | CalendarEvent): Promise<object> => {
  const allDay = record.all_day === true;
  const dtstart = dateConverterFromInputToServer(record.dtstart, allDay, 'start', record.tzid);
  const dtend = dateConverterFromInputToServer(record.dtend, allDay, 'end', record.tzid);
  const until =
    record.rrule?.until == null
      ? null
      : dateConverterFromInputToServer(record.rrule?.until, allDay, 'start', record.tzid); // Use 'start' for all-day events to prevent incrementing the day
  const rrule = until !== null ? { ...record.rrule, until } : record.rrule;
  const fixedRecord = { ...record, dtend, dtstart, rrule };
  return removeUnallowedFields(PersonalCalendarEventUpdateFields, fixedRecord);
};

const processAfterRead = async (
  record: PersonalCalendarEvent | CalendarEvent
): Promise<PersonalCalendarEvent | CalendarEvent> => {
  const allDay = record.all_day === true;
  const until =
    record.rrule?.until == null ? null : dateConverterFromServerToInput(record.rrule?.until, record.tzid, 'inclusive');

  const dtstart = allDay ? record.dtstart : dateConverterFromServerToInput(record.dtstart, record.tzid, 'inclusive');
  const dtend = allDay
    ? // If it's an all-day event, we need to convert the end date to be exclused, so we remove one day
      dateConverterFromServerToInput(record.dtend, 'utc', 'exclusive')
    : dateConverterFromServerToInput(record.dtend, record.tzid, 'inclusive');

  const rrule = until !== null ? { ...record.rrule, until } : record.rrule;
  return { ...record, dtstart, dtend, rrule: rrule as typeof record.rrule };
};

export const calendarEventCallbacks: ResourceCallbacks = {
  resource: CalendarEvents,
  beforeSave: async (r) => {
    const record = r as CalendarEvent;
    return processBeforeSave(record);
  },
  // Use processAfterRead for afterSave and afterRead because we want to keep UI consistency
  afterSave: async (r) => {
    const record = r as CalendarEvent;
    return processAfterRead(record);
  },
  afterRead: async (r) => {
    const record = r as CalendarEvent;
    return processAfterRead(record);
  },
};

export const personalCalendarEventCallbacks: ResourceCallbacks = {
  resource: `me/${CalendarEvents}`,
  beforeSave: async (r) => {
    const record = r as PersonalCalendarEvent;
    return processBeforeSave(record);
  },
  // Use processAfterRead for afterSave and afterRead because we want to keep UI consistency
  afterSave: async (r) => {
    const record = r as PersonalCalendarEvent;
    return processAfterRead(record);
  },
  afterRead: async (r) => {
    const record = r as PersonalCalendarEvent;
    return processAfterRead(record);
  },
};
